import { featureFlag } from "@/context/FeatureFlagProvider/featureFlags";
import {
  DRAWER_WIDTH_PX,
  TOP_NAV_MAITE_CONTENT_MAX_WIDTH_PX,
  TOP_NAV_MAITE_CONTENT_MIN_WIDTH_PX,
} from "@/Utils/GlobalStyle";
import { Box, Paper, styled, useTheme } from "@mui/material";
import { useFeatureGate } from "@statsig/react-bindings";
import React from "react";

interface ChatLayoutProps {
  children: React.ReactNode;
  sidebar?: React.ReactNode;
}

export const ChatLayout: React.FC<ChatLayoutProps> = ({ children, sidebar }) => {
  const { value: isTopNavLayoutEnabled } = useFeatureGate(featureFlag.topNavLayout);
  const theme = useTheme();

  const LayoutContainer = styled(Box)({
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    position: "relative",
    margin: "0",
    height: "calc(100vh - 62px)",
  });

  const SidebarContainer = styled(Box)({
    flexShrink: 0,
    width: `${DRAWER_WIDTH_PX}px`,
    position: "fixed",
    left: 0,
    top: "62px",
    height: "calc(100vh - 62px)",
    overflowY: "auto",
    overflowX: "visible",
    zIndex: 1000,
    borderRight: `1px solid ${theme.palette.navigation.border}`,
    "& > .MuiBox-root": {
      width: "inherit",
    },
  });

  const SidebarContentContainer = styled(Box)({
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    "& > .MuiBox-root:first-child": {
      padding: "12px 20px",
      marginLeft: "0",
      minWidth: "inherit",
      maxWidth: "inherit",
      "& > .MuiBox-root > button": {
        paddingLeft: "8px",
      },
      "& > .MuiButton-root": {
        padding: "4px 8px",
      },
      "& > .MuiTypography-root": {
        paddingLeft: "8px",
      },
    },
  });

  const ChatContentContainer = styled(Box)({
    minWidth: `${TOP_NAV_MAITE_CONTENT_MIN_WIDTH_PX}px`,
    maxWidth: `${TOP_NAV_MAITE_CONTENT_MAX_WIDTH_PX}px`,
    width: "100%",
    marginLeft: `${DRAWER_WIDTH_PX}px`,
    marginRight: "auto",
    paddingLeft: `calc((100vw - ${DRAWER_WIDTH_PX}px - ${TOP_NAV_MAITE_CONTENT_MAX_WIDTH_PX}px) / 2)`,
    height: "calc(100vh - 62px)",
    // overrides width of the chat content container here so that the changes are not added directly to the current layout
    // TODO: remove this once the new layout is fully rolled out in production
    "& .MuiStack-root": {
      maxWidth: `${TOP_NAV_MAITE_CONTENT_MAX_WIDTH_PX}px`,
      "& > .MuiStack-root": {
        maxWidth: `${TOP_NAV_MAITE_CONTENT_MAX_WIDTH_PX}px`,
        width: "inherit",
        "& > .MuiBox-root > .MuiBox-root": {
          maxWidth: `${TOP_NAV_MAITE_CONTENT_MAX_WIDTH_PX}px`,
        },
      },
    },
  });

  const TopNavLayout = ({
    children,
    sidebar,
  }: {
    children: React.ReactNode;
    sidebar: React.ReactNode;
  }) => {
    return (
      <LayoutContainer id="layout-container">
        {sidebar && (
          <SidebarContainer id="sidebar-container">
            <SidebarContentContainer id="sidebar-content-container">
              {sidebar}
            </SidebarContentContainer>
          </SidebarContainer>
        )}
        <ChatContentContainer id="chat-content-container">{children}</ChatContentContainer>
      </LayoutContainer>
    );
  };

  if (isTopNavLayoutEnabled) {
    return <TopNavLayout sidebar={sidebar}>{children}</TopNavLayout>;
  }

  return (
    <Paper sx={{ display: "flex", height: "100vh" }}>
      {sidebar && (
        <Box
          sx={{
            boxShadow: "4px 0px 4px -4px #00000040",
            borderRight: theme.palette.mode === "dark" ? "1px solid #4B4B4B" : undefined,
          }}
        >
          {sidebar}
        </Box>
      )}
      <Box sx={{ flexGrow: 1, overflow: "auto" }}>{children}</Box>
    </Paper>
  );
};
